import { baseClient } from '@/core/infrastructure/api/apiClient';
import { translate } from '@/core/utils/translate';
import {
  BulkDeleteRequestModel,
  BulkDeleteResponseModel,
  MarkAsReadRequestModel,
  MarkAsReadResponseModel,
  NotificationFilterModel,
  NotificationListRequestModel,
  NotificationListResponseModel,
} from './type';

export const notificationQueries = {
  keys: {
    all: ['notifications'] as const,
    list: () => [...notificationQueries.keys.all, 'list'] as const,
    notifications: (data: NotificationFilterModel) =>
      [...notificationQueries.keys.list(), { data }] as const,
    navbar: () => [...notificationQueries.keys.all, 'navbar'] as const, // Navbar için ayrı key
  },

  fn: {
    fetchNotifications: async (
      payload: NotificationListRequestModel
    ): Promise<NotificationListResponseModel> => {
      try {
        const response = await baseClient.post<NotificationListResponseModel>(
          '/user-notifications/search',
          payload
        );

        if (response.data?.header?.isSuccess) {
          return response.data;
        }

        throw new Error(
          response.data?.header?.message || translate('errors/api:notifications_fetch_failed')
        );
      } catch (error: any) {
        throw new Error(
          translate('errors/api:notifications_fetch_failed_with_error', {
            error: error.response?.data?.header?.message || error.message,
          })
        );
      }
    },

    // Navbar için SADECE okunmamış bildirimleri getir
    fetchNavbarNotifications: async (): Promise<NotificationListResponseModel> => {
      try {
        const payload = {
          index: 0,
          size: 10, // Navbar için sadece son 10 bildirim
          isRead: false, // SADECE okunmamış bildirimleri getir
        };

        const response = await baseClient.post<NotificationListResponseModel>(
          '/user-notifications/search',
          payload
        );

        if (response.data?.header?.isSuccess) {
          return response.data;
        }

        throw new Error(
          response.data?.header?.message ||
            translate('errors/api:navbar_notifications_fetch_failed')
        );
      } catch (error: any) {
        throw new Error(
          translate('errors/api:navbar_notifications_fetch_failed_with_error', {
            error: error.response?.data?.header?.message || error.message,
          })
        );
      }
    },
  },

  mutations: {
    markAsRead: async (
      payload: MarkAsReadRequestModel
    ): Promise<MarkAsReadResponseModel | null> => {
      try {
        const response = await baseClient.post<MarkAsReadResponseModel>(
          '/user-notifications/mark-as-read',
          payload
        );

        return response.data || null;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:notification_mark_read_failed', {
            error: error.response?.data?.header?.message || error.message,
          })
        );
      }
    },

    bulkDelete: async (
      payload: BulkDeleteRequestModel
    ): Promise<BulkDeleteResponseModel | null> => {
      try {
        const response = await baseClient.delete<BulkDeleteResponseModel>(
          '/user-notifications/bulk-delete',
          {
            data: payload,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        return response.data || null;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:notifications_delete_failed', {
            error: error.response?.data?.header?.message || error.message,
          })
        );
      }
    },

    deleteNotification: async (
      notificationId: string
    ): Promise<{ status: number; message?: string }> => {
      try {
        const response = await baseClient.delete(`/user-notifications/${notificationId}`);

        return {
          status: response.status,
          message: response.data?.header?.message,
        };
      } catch (error: any) {
        throw new Error(
          translate('errors/api:notification_delete_failed', {
            error: error.response?.data?.header?.message || error.message,
          })
        );
      }
    },

    bulkMarkAsRead: async (payload: { ids: string[]; isRead: boolean }): Promise<any> => {
      try {
        const response = await baseClient.patch('/user-notifications/bulk', payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        return response.data || null;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:notifications_bulk_mark_read_failed', {
            error: error.response?.data?.header?.message || error.message,
          })
        );
      }
    },

    markAsReadSingle: async (id: string, isRead: boolean): Promise<any> => {
      try {
        const response = await baseClient.patch(
          `/user-notifications/${id}`,
          { isRead },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        return response.data || null;
      } catch (error: any) {
        throw new Error(
          translate('errors/api:notification_single_mark_read_failed', {
            error: error.response?.data?.header?.message || error.message,
          })
        );
      }
    },
  },
};
